import Check from "@mui/icons-material/Check";
import RemoveFilled from "@mui/icons-material/Remove";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  GetReportDetailsQuery,
  PersonPickerFragment,
  PhoneType,
} from "generated-graphql/graphql";
import { useReviewerRemove } from "hooks/useAssignees";
import { useCallback, useMemo, useState } from "react";
import { formatPhoneNumber } from "util/phoneNumbers";
import { NoRowsOverlay } from "../../Inventory/Facility/NoRowsOverlay";
import PersonAdd from "@mui/icons-material/PersonAdd";
import { Reviewer } from "./ReviewForm";

type AddContactRow = {
  id: "add-contact";
  isAddContactRow: true;
};
type DataGridRow =
  | GetReportDetailsQuery["tierIIReport"]["reviewers"][number]
  | AddContactRow;

const isAddContactRow = (row: DataGridRow): row is AddContactRow => {
  return (row as AddContactRow).isAddContactRow;
};

export function ReviewersList({
  report = undefined,
  canDelete,
  setAddReviewerOpen,
  onSendEmail,
  isEmailLoading,
  canSendEmail,
  alreadySentRemindersList,
  currentUserIsReviewer,
  canVerifyAndSubmit,
}: {
  report?: GetReportDetailsQuery["tierIIReport"];
  canDelete: boolean;
  setAddReviewerOpen: (open: boolean) => void;
  onSendEmail?: (personId: string) => void;
  isEmailLoading?: boolean;
  canSendEmail: boolean;
  alreadySentRemindersList: string[];
  currentUserIsReviewer?: Reviewer;
  canVerifyAndSubmit: boolean;
}) {
  const alerts = useAlerts();
  const theme = useTheme();
  const [reviewerToDelete, setReviewerToDelete] =
    useState<PersonPickerFragment | null>(null);

  const rows = useMemo((): DataGridRow[] => {
    const reviewers = report?.reviewers ?? [];
    const baseRows: DataGridRow[] = [...reviewers];

    if (canVerifyAndSubmit) {
      baseRows.push({ id: "add-contact", isAddContactRow: true });
    }

    return baseRows;
  }, [report, canVerifyAndSubmit]);

  const [removeTierIIReportReviewers, { loading: removingReviewer }] =
    useReviewerRemove();

  const handleRemoveReviewer = useCallback(
    async (person: PersonPickerFragment | null) => {
      try {
        if (!report || !person) {
          setReviewerToDelete(null);
          return;
        }
        await removeTierIIReportReviewers({
          variables: { tierIIReportIds: [report.id], personIds: [person.id] },
        });
        alerts.success("Successfully removed reviewer");
      } catch (error) {
        alerts.error("There was a problem removing the reviewer");
      }
      setReviewerToDelete(null);
    },
    [report, removeTierIIReportReviewers, setReviewerToDelete, alerts]
  );

  const columns: GridColDef<DataGridRow>[] = useMemo(
    () => [
      {
        field: "isComplete",
        headerName: "Review Status",
        maxWidth: 180,
        headerAlign: "center",
        flex: 1,
        align: "left",
        colSpan: (_, row) => (isAddContactRow(row) ? 4 : undefined),
        renderCell: ({ row }) => {
          if (isAddContactRow(row)) {
            return (
              <Box display="flex" height="100%" alignItems="start">
                <Button
                  onClick={() => setAddReviewerOpen(true)}
                  startIcon={<PersonAdd fontSize="small" />}
                >
                  <Box>Add Reviewer</Box>
                </Button>
              </Box>
            );
          }
          return (
            <Chip
              label={row.isComplete ? "Complete" : "Incomplete"}
              variant="filled"
              color="default"
              size="small"
            />
          );
        },
      },
      {
        field: "name",
        headerName: "Reviewer Name",
        flex: 1,
        renderCell: ({ row }) => {
          if (isAddContactRow(row)) {
            return null;
          }
          if (row.person.id === currentUserIsReviewer?.person.id) {
            return (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2">
                  {getFullName(row.person)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  (me)
                </Typography>
              </Stack>
            );
          }
          return getFullName(row.person);
        },
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 1.25,
        valueGetter: (_, row) => {
          if (isAddContactRow(row)) {
            return null;
          }
          const phone =
            row.person.phones?.find((p) => p.type === PhoneType.Work) ??
            row.person.phones?.[0];
          return phone ? formatPhoneNumber(phone) : "";
        },
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        valueGetter: (_, row) => {
          if (isAddContactRow(row)) {
            return null;
          }
          return row.person.email;
        },
      },
      {
        field: "actions",
        type: "actions",
        cellClassName: "actions",
        align: "right",
        flex: 0.5,
        renderCell: ({ row }) => {
          if (isAddContactRow(row)) {
            return null;
          }
          return (
            <Box display="flex" height="100%" alignItems="center">
              {!row.isComplete &&
                onSendEmail &&
                canSendEmail &&
                canVerifyAndSubmit &&
                !alreadySentRemindersList?.includes(row.id) && (
                  <Tooltip title="Send reminder email to reviewer" key="email">
                    <GridActionsCellItem
                      label="Email"
                      icon={<EmailOutlined />}
                      onClick={() => onSendEmail(row.person.id)}
                      disabled={isEmailLoading}
                    />
                  </Tooltip>
                )}
              {canDelete && !isAddContactRow(row) && (
                <Tooltip title="Remove reviewer" key="delete">
                  <GridActionsCellItem
                    label="Delete"
                    icon={<RemoveFilled />}
                    onClick={() => setReviewerToDelete(row.person)}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [
      canDelete,
      onSendEmail,
      canSendEmail,
      canVerifyAndSubmit,
      alreadySentRemindersList,
      currentUserIsReviewer?.person.id,
      isEmailLoading,
      setAddReviewerOpen,
    ]
  );

  return (
    <>
      <DataGrid<DataGridRow>
        disableRowSelectionOnClick={true}
        columns={columns}
        initialState={{
          sorting: { sortModel: [{ field: "name", sort: "desc" }] },
        }}
        rows={rows}
        hideFooter
        slots={{
          noRowsOverlay: () => (
            <NoRowsOverlay
              sx={{ maxWidth: 600 }}
              message="If your organization requires other individuals to review this report, you can add them here. They'll be emailed when you mark the report as ready for review."
            />
          ),
        }}
        autoHeight
        getRowHeight={() => "auto"}
        sx={{
          "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
            { outline: "none" },
          "& .MuiDataGrid-row:hover": { cursor: "pointer" },
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell:not(.actions)":
            { py: "9px" },
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
            py: "4px",
            alignItems: "start",
          },
        }}
      />
      <ConfirmDialog
        open={!!reviewerToDelete}
        header="Are you sure you want to remove this reviewer from the report?"
        resources={[
          {
            resourceType: "Reviewer Name",
            resourceName: `${reviewerToDelete?.first} ${reviewerToDelete?.last}`,
          },
        ]}
        confirmText="Yes, Remove"
        onClose={() => setReviewerToDelete(null)}
        onConfirm={() => handleRemoveReviewer(reviewerToDelete)}
        loading={removingReviewer}
      />
    </>
  );
}
